/**
 * One JSON file for a whole conversation (TRA-1544).
 *
 * Built in the browser from the documents the page already loaded, so no
 * endpoint is involved and an 18 MB run writes straight to disk (Chrome's
 * "Copy response" silently drops bodies that size). Three parts, top to
 * bottom: a summary, the turns labeled the way the transcript page labels
 * them (same parser, never a second one), and the raw trace documents
 * exactly as the API returned them, for replay.
 */

import type { RunListItem, TraceDocument, TraceServedBy } from '@/api/generated/model'
import {
  buildTurns,
  type TranscriptEvent,
  type TranscriptTurn,
} from '@/components/review/transcript'
import { docToTurn } from '@/components/review/turns'
import { downloadTextFile } from '@/lib/download'

export const CONVERSATION_EXPORT_FORMAT = 'trainloop.conversation.v1'

/** Above this many characters the file is written compact, not indented. */
const PRETTY_PRINT_LIMIT = 25 * 1024 * 1024

export interface ConversationExportMeta {
  /** "Conversation" or "Trace": which door the page was opened through. */
  idLabel: string
  idValue: string
  sourceUrl: string
  exportedAt: Date
}

export interface ExportTokens {
  prompt: number
  completion: number
  total: number
}

export type ExportEvent =
  | { kind: 'system' | 'text' | 'reasoning'; trace_id: string; text: string; chars: number }
  | {
      kind: 'user'
      trace_id: string
      text: string
      chars: number
      /** Image URLs (or data URLs) the user message carried, as the parser read them. */
      images: string[]
    }
  | {
      kind: 'tool_call'
      trace_id: string
      tool: string
      call_id: string | null
      arguments: string
      argument_chars: number
    }
  | {
      kind: 'tool_result'
      trace_id: string
      tool: string
      call_id: string | null
      /** Trace whose request carried the result; usually the next call. */
      result_trace_id: string | null
      text: string
      chars: number
    }
  | { kind: 'raw'; trace_id: string; label: string; text: string; chars: number }

export interface ExportTurn {
  index: number
  trace_id: string
  trace_key: string | null
  timestamp: string
  latency_ms: number
  status_code: number
  model: { requested: string | null; actual: string | null }
  served_by: TraceServedBy | null
  tokens: ExportTokens | null
  truncated: { request: boolean; response: boolean }
  message_count: number
  new_message_count: number
  tool_count: number
  error_message: string | null
  events: ExportEvent[]
}

export interface ConversationExport {
  format: typeof CONVERSATION_EXPORT_FORMAT
  exported_at: string
  source_url: string
  conversation: {
    /** `trace_id` only when a conversation-less run's document carries no key. */
    id_kind: 'conversation_id' | 'trace_key' | 'trace_id'
    id: string
    first_timestamp: string | null
    last_timestamp: string | null
    model_calls: number
  }
  run: {
    models: string[]
    environment: string | null
    traffic_type: string | null
    tenant_id: string | null
    tenant_name: string | null
    tokens: ExportTokens
    /** Deployment that answered the last call that recorded one. */
    served_by: TraceServedBy | null
  }
  notes: string[]
  turns: ExportTurn[]
  raw_traces: readonly TraceDocument[]
}

function exportEvents(events: TranscriptEvent[]): ExportEvent[] {
  const out: ExportEvent[] = []
  for (const event of events) {
    switch (event.kind) {
      case 'thinking':
        out.push({ kind: 'reasoning', trace_id: event.traceId, text: event.text, chars: event.text.length })
        break
      case 'assistant':
        out.push({ kind: 'text', trace_id: event.traceId, text: event.text, chars: event.text.length })
        break
      case 'user':
        out.push({
          kind: 'user',
          trace_id: event.traceId,
          text: event.text,
          chars: event.text.length,
          images: event.images,
        })
        break
      case 'system':
        out.push({ kind: 'system', trace_id: event.traceId, text: event.text, chars: event.text.length })
        break
      case 'tool':
        out.push({
          kind: 'tool_call',
          trace_id: event.traceId,
          tool: event.name,
          call_id: event.callId,
          arguments: event.args,
          argument_chars: event.args.length,
        })
        if (event.result !== null) {
          out.push({
            kind: 'tool_result',
            trace_id: event.traceId,
            tool: event.name,
            call_id: event.callId,
            result_trace_id: event.resultTraceId,
            text: event.result,
            chars: event.result.length,
          })
        }
        break
      case 'raw':
        out.push({ kind: 'raw', trace_id: event.traceId, label: event.label, text: event.text, chars: event.text.length })
        break
    }
  }
  return out
}

function docTokens(doc: TraceDocument): ExportTokens | null {
  const usage = doc.tokenUsage
  if (usage === undefined || usage === null) return null
  return { prompt: usage.promptTokens, completion: usage.completionTokens, total: usage.totalTokens }
}

function sumTokens(docs: readonly TraceDocument[]): ExportTokens {
  const sum: ExportTokens = { prompt: 0, completion: 0, total: 0 }
  for (const doc of docs) {
    const tokens = docTokens(doc)
    if (tokens === null) continue
    sum.prompt += tokens.prompt
    sum.completion += tokens.completion
    sum.total += tokens.total
  }
  return sum
}

function exportTurn(doc: TraceDocument, turn: TranscriptTurn): ExportTurn {
  return {
    index: turn.index,
    trace_id: doc.traceId,
    trace_key: doc.traceKey ?? null,
    timestamp: doc.timestamp,
    latency_ms: doc.latencyMs,
    status_code: doc.statusCode,
    model: { requested: doc.requestModel ?? null, actual: doc.actualModel ?? null },
    served_by: doc.servedBy ?? null,
    tokens: docTokens(doc),
    truncated: { request: doc.request.truncated === true, response: doc.response.truncated === true },
    message_count: turn.messageCount,
    new_message_count: turn.newMessageCount,
    tool_count: turn.toolCount,
    error_message: doc.errorMessage ?? null,
    events: exportEvents(turn.events),
  }
}

/** The replay limitation of one call's stored request body, or null when it has none. */
function requestBodyNote(doc: TraceDocument, call: string): string | null {
  if (doc.request.bodyText === undefined || doc.request.bodyText === null) {
    return `Call ${call}: the request body was not captured, so this call cannot be replayed.`
  }
  if (doc.request.truncated === true) {
    return `Call ${call}: the request body was cut at the capture cap, so a replay of it is incomplete.`
  }
  const delta = doc.requestDelta
  if (delta === undefined || delta === null || delta.fromIndex === 0) return null
  // The gateway stored only the messages this call added; the labeled turn
  // above has the whole thread, the raw body does not. The parent trace can
  // be a delta itself, so the earlier messages sit along its chain.
  const parent = delta.baseTraceKey == null
    ? 'the earlier calls'
    : `trace ${delta.baseTraceKey} and the traces before it`
  const total = String(delta.fullMessageCount)
  if (delta.fromIndex >= delta.fullMessageCount) {
    // The client re-sent history the gateway already had, so nothing was added.
    return `Call ${call}: the stored request body holds no new messages; a replay needs all ${total} from ${parent}.`
  }
  return (
    `Call ${call}: the stored request body holds only messages ${String(delta.fromIndex + 1)} to ` +
    `${total} of ${total}; a replay needs the earlier messages from ${parent}.`
  )
}

function callList(calls: readonly number[]): string {
  return `${calls.length === 1 ? 'call' : 'calls'} ${calls.join(', ')}`
}

/**
 * What limits a replay of this file or its totals, one line each. Empty
 * when nothing does.
 */
function exportNotes(docs: readonly TraceDocument[]): string[] {
  const notes: string[] = []
  const unrecorded: number[] = []
  const withoutUsage: number[] = []
  docs.forEach((doc, i) => {
    const note = requestBodyNote(doc, String(i + 1))
    if (note !== null) notes.push(note)
    if (doc.servedBy === undefined || doc.servedBy === null) unrecorded.push(i + 1)
    if (docTokens(doc) === null) withoutUsage.push(i + 1)
  })
  if (withoutUsage.length > 0) {
    notes.push(`Token totals leave out ${callList(withoutUsage)}: no token usage was recorded.`)
  }
  if (unrecorded.length === docs.length && docs.length > 0) {
    notes.push('Deployment not recorded for any call in this conversation (the traces carry no servedBy).')
  } else if (unrecorded.length > 0) {
    notes.push(`Deployment not recorded for ${callList(unrecorded)}.`)
  }
  return notes
}

function lastServedBy(docs: readonly TraceDocument[]): TraceServedBy | null {
  for (let i = docs.length - 1; i >= 0; i -= 1) {
    const servedBy = docs[i]?.servedBy
    if (servedBy !== undefined && servedBy !== null) return servedBy
  }
  return null
}

function runSummary(
  docs: readonly TraceDocument[],
  run: RunListItem | undefined,
): ConversationExport['run'] {
  const last = docs[docs.length - 1]
  const models = [...new Set(docs.flatMap((doc) => (doc.requestModel == null ? [] : [doc.requestModel])))]
  return {
    models: run === undefined ? models : run.models,
    environment: run === undefined ? last?.environment ?? null : run.environment,
    traffic_type: run === undefined ? last?.trafficType ?? null : run.traffic_type,
    tenant_id: run === undefined ? last?.tenantId ?? null : run.client_tenant_id,
    tenant_name: run === undefined ? last?.tenantName ?? null : run.client_tenant_name,
    // The run listing carries no token totals, so they always come from the docs.
    tokens: sumTokens(docs),
    served_by: lastServedBy(docs),
  }
}

/**
 * The identifier that reopens this run. The trace door shows the short
 * trace id on the page, but only the full storage key opens the run again,
 * so the file carries the key from the document itself.
 */
function conversationIdentity(
  docs: readonly TraceDocument[],
  meta: ConversationExportMeta,
): Pick<ConversationExport['conversation'], 'id_kind' | 'id'> {
  if (meta.idLabel === 'Conversation') return { id_kind: 'conversation_id', id: meta.idValue }
  const traceKey = docs[0]?.traceKey
  if (traceKey === undefined || traceKey === null) return { id_kind: 'trace_id', id: meta.idValue }
  return { id_kind: 'trace_key', id: traceKey }
}

/** Assemble the export of `docs` (oldest first). Pure; nothing is written. */
export function buildConversationExport(
  docs: readonly TraceDocument[],
  run: RunListItem | undefined,
  meta: ConversationExportMeta,
): ConversationExport {
  const turns = buildTurns(docs.map(docToTurn))
  return {
    format: CONVERSATION_EXPORT_FORMAT,
    exported_at: meta.exportedAt.toISOString(),
    source_url: meta.sourceUrl,
    conversation: {
      ...conversationIdentity(docs, meta),
      first_timestamp: docs[0]?.timestamp ?? null,
      last_timestamp: docs[docs.length - 1]?.timestamp ?? null,
      model_calls: docs.length,
    },
    run: runSummary(docs, run),
    notes: exportNotes(docs),
    turns: docs.map((doc, i) => exportTurn(doc, turns[i] as TranscriptTurn)),
    raw_traces: docs,
  }
}

/** `conversation-<id>-<YYYYMMDD-HHMM>.json`; a trace key keeps only its last path segment. */
export function conversationExportFilename(meta: ConversationExportMeta): string {
  const lastSegment = meta.idValue.split('/').pop() ?? meta.idValue
  const id = lastSegment.replace(/\.json$/, '').replace(/[^A-Za-z0-9._-]/g, '_')
  const pad = (n: number): string => n.toString().padStart(2, '0')
  const at = meta.exportedAt
  const stamp =
    `${at.getFullYear().toString()}${pad(at.getMonth() + 1)}${pad(at.getDate())}` +
    `-${pad(at.getHours())}${pad(at.getMinutes())}`
  return `conversation-${id}-${stamp}.json`
}

/** Indented when the file stays under the pretty-print limit, else compact. */
export function serializeConversationExport(data: ConversationExport): string {
  const pretty = JSON.stringify(data, null, 2)
  return pretty.length <= PRETTY_PRINT_LIMIT ? pretty : JSON.stringify(data)
}

/** The Download button's action: build the file and hand it to the browser. */
export function downloadConversationExport(
  docs: readonly TraceDocument[],
  run: RunListItem | undefined,
  meta: ConversationExportMeta,
): void {
  const data = buildConversationExport(docs, run, meta)
  downloadTextFile(serializeConversationExport(data), conversationExportFilename(meta), 'application/json')
}
